import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import { ApiLogDetail, ApiLogRow, FLAG_LABELS, LogFilters, RateLimits, SERIOUS_FLAGS, getPublicApiLog, getPublicApiLogs } from "interfaces/publicApi";

/** Why the limiter refused a call (BO only — the client just gets 429). */
export const REASON_LABELS: Record<string, string> = {
    cooldown: "en pause",
    interval: "trop rapproché",
    daily: "quota du jour atteint",
    minute: "plafond serveur (120/min)",
};

/** Ready-made limiter profiles, editable before saving. */
export const LIMIT_PRESETS: { label: string; limits: RateLimits }[] = [
    { label: "Cahier des charges : 4 appels → 1 min, 4 → 5 min", limits: { minIntervalSec: 1, burst: 4, cooldownsMin: [1, 5], resetAfterMin: 60, maxPerDay: 200, maxResults: 20 } },
    { label: "1 appel / seconde, pause 5 min tous les 10 appels", limits: { minIntervalSec: 1, burst: 10, cooldownsMin: [5], resetAfterMin: 60, maxPerDay: 300, maxResults: 20 } },
    { label: "1 appel toutes les 5 minutes", limits: { minIntervalSec: 300, burst: 0, cooldownsMin: [], resetAfterMin: 60, maxPerDay: 0, maxResults: 20 } },
    { label: "Prudent : 1 appel / min, 50 par jour, 10 résultats", limits: { minIntervalSec: 60, burst: 0, cooldownsMin: [], resetAfterMin: 60, maxPerDay: 50, maxResults: 10 } },
];

const dur = (sec: number) => (sec < 60 ? `${sec} s` : sec % 3600 === 0 ? `${sec / 3600} h` : sec % 60 === 0 ? `${sec / 60} min` : `${Math.round(sec / 6) / 10} min`);

/** A profile in one line, e.g. "1 s · 4 d'affilée → pause 1 min, 5 min · 200/j · 20 résultats". */
export function describeLimits(l: RateLimits): string {
    const parts: string[] = [];
    parts.push(l.minIntervalSec > 0 ? `1 appel / ${dur(l.minIntervalSec)}` : "pas de délai");
    if (l.burst > 0) parts.push(`${l.burst} d'affilée → pause ${l.cooldownsMin.map((c) => dur(c * 60)).join(", ")}`);
    parts.push(l.maxPerDay > 0 ? `${l.maxPerDay}/jour` : "sans plafond/jour");
    parts.push(`${l.maxResults} résultats/appel`);
    return parts.join(" · ");
}

export function formatRelative(d?: string | null) {
    if (!d) return "—";
    const date = new Date(d);
    const diffMins = Math.floor((Date.now() - date.getTime()) / 60000);
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffMins < 1440) return `Il y a ${Math.floor(diffMins / 60)} h`;
    if (diffMins < 7 * 1440) return `Il y a ${Math.floor(diffMins / 1440)} j`;
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

export function formatDateTime(d?: string | null) {
    if (!d) return "—";
    return new Date(d).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function formatDuration(sec?: number | null) {
    if (sec === null || sec === undefined) return "—";
    if (sec < 60) return `${sec} s`;
    if (sec < 3600) return `${Math.round(sec / 60)} min`;
    if (sec < 86400) return `${Math.round((sec / 3600) * 10) / 10} h`;
    return `${Math.round((sec / 86400) * 10) / 10} j`;
}

export const n = (v?: number | null) => (v ?? 0).toLocaleString("fr-FR");

export function FlagBadge({ flag, count }: { flag: string; count?: number }) {
    const serious = SERIOUS_FLAGS.has(flag);
    return (
        <span
            title={flag}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                serious ? "bg-red-50 text-red-700 border-red-100" : "bg-amber-50 text-amber-800 border-amber-100"
            }`}
        >
            {serious && <AlertTriangle className="w-3 h-3" />}
            {FLAG_LABELS[flag] || flag}
            {count !== undefined && <span className="font-semibold">· {count}</span>}
        </span>
    );
}

export function StatusBadge({ status }: { status: number }) {
    const cls = status >= 500 ? "bg-red-600 text-white" : status >= 400 ? "bg-red-50 text-red-700 border border-red-100" : "bg-green-50 text-green-700 border border-green-100";
    return <span className={`inline-block w-10 text-center px-1 py-0.5 rounded text-[11px] font-medium ${cls}`}>{status}</span>;
}

export function Card({ label, value, sub, tone }: { label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: "danger" | "ok" }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-1">{label}</p>
            <p className={`text-2xl font-semibold ${tone === "danger" ? "text-red-600" : tone === "ok" ? "text-green-700" : "text-gray-900"}`}>{value}</p>
            {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
    );
}

export function ProgressBar({ percent }: { percent: number }) {
    const p = Math.max(0, Math.min(100, percent));
    return (
        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#1A1A1A]" style={{ width: `${p}%` }} />
        </div>
    );
}

/** One call, with the exact establishments it returned. */
export function LogDetailDrawer({ logId, onClose }: { logId: string; onClose: () => void }) {
    const [detail, setDetail] = useState<ApiLogDetail | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setDetail(null);
        setError(null);
        getPublicApiLog(logId)
            .then(setDetail)
            .catch(() => setError("Impossible de charger cet appel"));
    }, [logId]);

    const log = detail?.log;
    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/20" onClick={onClose}>
            <div className="w-full max-w-3xl h-full bg-white shadow-xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-3 flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">Détail de l'appel</p>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-900">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                {error && <p className="p-5 text-sm text-red-600">{error}</p>}
                {!detail && !error && <p className="p-5 text-sm text-gray-400">Chargement…</p>}
                {log && (
                    <div className="p-5 space-y-5">
                        <div className="flex items-center gap-2 flex-wrap">
                            <StatusBadge status={log.status} />
                            <span className="font-mono text-sm text-gray-900 break-all">
                                <span className="text-gray-400 mr-1">{log.method}</span>
                                {log.url}
                            </span>
                        </div>
                        {log.flags.length > 0 && (
                            <div className="flex gap-1 flex-wrap">
                                {log.flags.map((f) => (
                                    <FlagBadge key={f} flag={f} />
                                ))}
                            </div>
                        )}
                        <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-xs">
                            <Info label="Date" value={formatDateTime(log.createdAt)} />
                            <Info label="Client" value={detail.user ? <Link className="underline" to={`/bo/public-api/${detail.user._id}`}>{detail.user.email}</Link> : log.userEmail || "—"} />
                            <Info label="Clé" value={log.apiKeyPrefix ? <span className="font-mono">{log.apiKeyPrefix}…</span> : "—"} />
                            <Info label="Endpoint" value={log.endpoint} />
                            <Info label="Code erreur" value={log.errorCode || "—"} />
                            <Info label="Durée" value={`${log.durationMs} ms`} />
                            <Info label="Propriétaires / éléments" value={n(log.results)} />
                            <Info label="Établissements renvoyés" value={n(log.establishments)} />
                            <Info label="Reçus pour la première fois" value={n(log.newEstablishments)} />
                            <Info label="Limiteur" value={log.limitReason ? <span className="text-red-600">refusé : {REASON_LABELS[log.limitReason] || log.limitReason}</span> : log.rate ? `série ${log.rate.series} · palier ${log.rate.level} · ${log.rate.dayCount} appels aujourd'hui` : "—"} />
                            <Info label="Retry-After renvoyé" value={formatDuration(log.retryAfterSec)} />
                            <Info label="Couverture après l'appel" value={log.deliveredAfter !== undefined && log.baseSize ? `${n(log.deliveredAfter)} / ${n(log.baseSize)} (${Math.round((log.deliveredAfter / log.baseSize) * 1000) / 10} %)` : "—"} />
                            <Info label="IP" value={<span className="font-mono">{log.ip || "—"}</span>} />
                            <Info label="X-Forwarded-For" value={<span className="font-mono break-all">{log.forwardedFor || "—"}</span>} />
                            <Info label="Groupes examinés" value={log.scanned !== undefined ? n(log.scanned) : "—"} />
                            <Info label="User-Agent" value={<span className="break-all">{log.userAgent || "—"}</span>} wide />
                            <Info label="Request id" value={<span className="font-mono">{log.requestId}</span>} wide />
                        </dl>
                        {log.query && (
                            <div>
                                <p className="text-xs font-medium text-gray-600 mb-1">Paramètres</p>
                                <pre className="text-[11px] bg-gray-50 border border-gray-100 rounded p-2 overflow-x-auto">{JSON.stringify(log.query, null, 2)}</pre>
                            </div>
                        )}
                        {log.body && (
                            <div>
                                <p className="text-xs font-medium text-gray-600 mb-1">Corps</p>
                                <pre className="text-[11px] bg-gray-50 border border-gray-100 rounded p-2 overflow-x-auto">{JSON.stringify(log.body, null, 2)}</pre>
                            </div>
                        )}
                        <div>
                            <p className="text-xs font-medium text-gray-600 mb-2">
                                Établissements renvoyés ({detail.establishments.length}) — <span className="text-green-700">nouveaux</span> = reçus pour la première fois par cet appel
                            </p>
                            {detail.establishments.length === 0 ? (
                                <p className="text-xs text-gray-400">Aucun.</p>
                            ) : (
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-gray-500 border-b border-gray-100">
                                            <th className="text-left py-1.5">Nom</th>
                                            <th className="text-left py-1.5">SIRET</th>
                                            <th className="text-left py-1.5">Ville</th>
                                            <th className="text-left py-1.5">Franchise</th>
                                            <th className="text-right py-1.5">Statut</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detail.establishments.map((e) => (
                                            <tr key={e.id} className="border-b border-gray-50">
                                                <td className="py-1.5 text-gray-900">{e.name || "—"}</td>
                                                <td className="py-1.5 font-mono text-gray-600">{e.siret || "—"}</td>
                                                <td className="py-1.5 text-gray-600">{[e.postcode, e.city].filter(Boolean).join(" ") || "—"}</td>
                                                <td className="py-1.5 text-gray-600">{e.franchise || "—"}</td>
                                                <td className="py-1.5 text-right">{e.new ? <span className="text-green-700 font-medium">nouveau</span> : <span className="text-gray-400">déjà reçu</span>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Info({ label, value, wide }: { label: string; value: React.ReactNode; wide?: boolean }) {
    return (
        <div className={wide ? "col-span-2 md:col-span-3" : ""}>
            <dt className="text-gray-400">{label}</dt>
            <dd className="text-gray-900 mt-0.5">{value}</dd>
        </div>
    );
}

/** Paginated call log with filters; rows open the call detail. */
export function CallsTable({ userId, showUser = true, title = "Appels" }: { userId?: string; showUser?: boolean; title?: string }) {
    const [rows, setRows] = useState<ApiLogRow[]>([]);
    const [nextBefore, setNextBefore] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<"" | "ok" | "error">("");
    const [flaggedOnly, setFlaggedOnly] = useState(false);
    const [flag, setFlag] = useState("");
    const [endpoint, setEndpoint] = useState("");
    const [open, setOpen] = useState<string | null>(null);

    const filters = (before?: string): LogFilters => ({
        userId,
        status: status || undefined,
        flagged: flaggedOnly,
        flag: flag || undefined,
        endpoint: endpoint || undefined,
        before,
        limit: 50,
    });

    const load = async (append = false) => {
        setLoading(true);
        try {
            const res = await getPublicApiLogs(filters(append ? nextBefore || undefined : undefined));
            setRows((r) => (append ? [...r, ...res.logs] : res.logs));
            setNextBefore(res.nextBefore);
        } catch {
            if (!append) setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, status, flaggedOnly, flag, endpoint]);

    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-900">{title}</p>
                <div className="flex flex-wrap items-center gap-2">
                    <select value={endpoint} onChange={(e) => setEndpoint(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
                        <option value="">Tous les endpoints</option>
                        {["GET /api-client/owners", "GET /api-client/owners/:id/establishments", "GET /api-client/franchises", "GET /api-client/franchises/:id", "GET /api-client/news", "GET /api-client/account", "GET /api-client/reports", "POST /api-client/establishments/:id/report", "GET /api-client"].map((e) => (
                            <option key={e} value={e}>
                                {e}
                            </option>
                        ))}
                    </select>
                    <select value={status} onChange={(e) => setStatus(e.target.value as any)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
                        <option value="">Tous statuts</option>
                        <option value="ok">Succès</option>
                        <option value="error">Erreurs</option>
                    </select>
                    <select value={flag} onChange={(e) => setFlag(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
                        <option value="">Tous signaux</option>
                        {Object.entries(FLAG_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>
                                {v}
                            </option>
                        ))}
                    </select>
                    <label className="flex items-center gap-1.5 text-xs text-gray-600">
                        <input type="checkbox" checked={flaggedOnly} onChange={(e) => setFlaggedOnly(e.target.checked)} />
                        Signalés uniquement
                    </label>
                    <button onClick={() => load(false)} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50">
                        Rafraîchir
                    </button>
                </div>
            </div>
            {rows.length === 0 ? (
                <p className="px-4 py-8 text-center text-xs text-gray-400">{loading ? "Chargement…" : "Aucun appel."}</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-100 text-xs text-gray-500">
                                <th className="text-left px-4 py-2 font-medium">Date</th>
                                {showUser && <th className="text-left px-4 py-2 font-medium">Client</th>}
                                <th className="text-left px-4 py-2 font-medium">Statut</th>
                                <th className="text-left px-4 py-2 font-medium">Appel</th>
                                <th className="text-right px-4 py-2 font-medium">Résultats</th>
                                <th className="text-right px-4 py-2 font-medium">Nouveaux</th>
                                <th className="text-left px-4 py-2 font-medium">Limiteur</th>
                                <th className="text-left px-4 py-2 font-medium">IP</th>
                                <th className="text-left px-4 py-2 font-medium">Signaux</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((l) => (
                                <tr key={l._id} onClick={() => setOpen(l._id)} className="border-b border-gray-50 hover:bg-[#F9F6F3] cursor-pointer text-xs">
                                    <td className="px-4 py-2 text-gray-500 whitespace-nowrap" title={formatDateTime(l.createdAt)}>
                                        {formatDateTime(l.createdAt)}
                                    </td>
                                    {showUser && <td className="px-4 py-2 text-gray-800">{l.userEmail || <span className="text-gray-400">{l.apiKeyPrefix ? `${l.apiKeyPrefix}…` : "inconnu"}</span>}</td>}
                                    <td className="px-4 py-2">
                                        <StatusBadge status={l.status} />
                                    </td>
                                    <td className="px-4 py-2 font-mono text-gray-800 max-w-[420px] truncate" title={l.url}>
                                        <span className="text-gray-400 mr-1">{l.method}</span>
                                        {l.url}
                                        {l.errorCode && <span className="ml-2 text-red-600 font-sans">{l.errorCode}</span>}
                                    </td>
                                    <td className="px-4 py-2 text-right text-gray-900" title={l.establishments ? `${l.results} propriétaire(s) / élément(s)` : undefined}>{n(l.establishments || l.results)}</td>
                                    <td className="px-4 py-2 text-right font-medium text-gray-900">{l.newEstablishments ? n(l.newEstablishments) : <span className="text-gray-300">0</span>}</td>
                                    <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                                        {l.limitReason ? <span className="text-red-600">{REASON_LABELS[l.limitReason] || l.limitReason}</span> : l.rate ? <span className="text-gray-400">série {l.rate.series} · {l.rate.dayCount}/j</span> : "—"}
                                    </td>
                                    <td className="px-4 py-2 font-mono text-gray-500">{l.ip || "—"}</td>
                                    <td className="px-4 py-2">
                                        <div className="flex gap-1 flex-wrap">
                                            {l.flags.map((f) => (
                                                <FlagBadge key={f} flag={f} />
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {nextBefore && (
                <div className="px-4 py-3 border-t border-gray-100 text-center">
                    <button onClick={() => load(true)} disabled={loading} className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50">
                        {loading ? "Chargement…" : "Plus d'appels"}
                    </button>
                </div>
            )}
            {open && <LogDetailDrawer logId={open} onClose={() => setOpen(null)} />}
        </div>
    );
}
